import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import PageMeta from '../SEO/PageMeta';
import { AnswerBlock, QuestionCard } from './QuestionCard';
import PublicFooter from '../PublicFooter/PublicFooter';
import './CardBrowser.css';
import './PublicCardPage.css';

const BACKEND = 'https://civilfox-backend.onrender.com';

function answerText(card) {
  const correct = Array.isArray(card.options)
    ? card.options.find(option => option?.isCorrect)?.text
    : '';
  return [correct || card.answer, card.explanation].filter(Boolean).join(' — ').slice(0, 500);
}

function questionJsonLd(card, answer) {
  if (!card?.question || !answer) return null;
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Question',
    name: String(card.question).slice(0, 110),
    text: card.question,
    answerCount: 1,
    ...(card.subject ? { about: card.subject } : {}),
    acceptedAnswer: { '@type': 'Answer', text: answer },
  }).replace(/</g, '\\u003c');
}

export default function PublicCardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(window.__CARD_DATA__ || null);
  const [loading, setLoading] = useState(!window.__CARD_DATA__);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (window.__CARD_DATA__ && (window.__CARD_DATA__._id === id || window.__CARD_DATA__.slug === id)) {
      window.__CARD_DATA__ = null;
      return undefined;
    }

    let active = true;
    setCard(null);
    setLoading(true);
    setError('');
    setPicked(null);
    setRevealed(false);

    axios.get(`${BACKEND}/api/public/card/${id}`)
      .then(response => {
        if (!active) return;
        const sharedCard = response.data?.card;
        if (sharedCard) setCard(sharedCard);
        else setError('This card may have been removed or the link is incorrect.');
      })
      .catch(requestError => {
        if (!active) return;
        if (requestError.response?.status === 404) setError('This card may have been removed or the link is incorrect.');
        else setError('Could not load this shared card. Please check your connection and try again.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    document.documentElement.classList.add('public-card-page');
    document.body.classList.add('public-card-page');
    return () => {
      document.documentElement.classList.remove('public-card-page');
      document.body.classList.remove('public-card-page');
    };
  }, []);

  useEffect(() => {
    if (card?.slug && card.slug !== id) {
      navigate(`/card/${encodeURIComponent(card.slug)}`, { replace: true });
    }
  }, [card?.slug, id, navigate]);

  const canonicalPath = `/card/${encodeURIComponent(card?.slug || id)}`;
  const answer = card ? answerText(card) : '';
  const jsonLd = card ? questionJsonLd(card, answer) : null;
  const hasOptions = Array.isArray(card?.options) && card.options.length > 0;
  const done = hasOptions ? picked !== null : revealed;

  return (
    <div className="cb cb-public-card">
      {card && (
        <PageMeta
          title={`${String(card.question || 'UPSC Practice Question').slice(0, 75)} | CivilFox`}
          description={`${card.examType || 'UPSC'} ${card.subject || ''} practice question: ${String(card.question || '').slice(0, 140)}`}
          path={canonicalPath}
          type="article"
          structuredData={jsonLd ? JSON.parse(jsonLd) : undefined}
        />
      )}

      <header className="cb-public-header">
        <Link to="/" className="cb-public-brand" aria-label="CivilFox home">CivilFox</Link>
        <Link to="/register" className="cb-public-signup">Sign up free</Link>
      </header>

      <main className="cb-content cb-public-content">
        <nav className="cb-public-breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span aria-hidden="true">/</span>
          <span>{card?.subject || 'Shared question'}</span>
        </nav>

        {loading && (
          <div className="cb-state" aria-busy="true">
            <span className="cb-spin" role="status" aria-label="Loading shared card" />
          </div>
        )}

        {!loading && error && (
          <section className="cb-public-message" role="alert">
            <h1>Card unavailable</h1>
            <p>{error}</p>
            <Link to="/browse">Browse study cards</Link>
          </section>
        )}

        {!loading && card && (
          <article className="cb-public-article">
            <div className="cb-public-title">
              <p>Shared study card</p>
              <h1>{card.subject || 'Exam'} practice question</h1>
            </div>
            <QuestionCard
              card={card}
              picked={picked}
              done={done}
              onPick={setPicked}
            />
            {hasOptions && done && <AnswerBlock card={card} />}
            {!hasOptions && !revealed && (
              <button type="button" className="cb-public-reveal" onClick={() => setRevealed(true)}>
                Show answer
              </button>
            )}
            {!hasOptions && revealed && <AnswerBlock card={card} />}
          </article>
        )}
      </main>
      <PublicFooter />
    </div>
  );
}
